<script setup>
import { ref, reactive, onMounted, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useModuleGate } from 'dashboard/composables/useModuleGate';
import { useAlert } from 'dashboard/composables';
import googleAdsAPI from 'dashboard/api/googleAds';

// 26/08: conectar o Google Ads consome CONEXÃO do Estúdio Social — gate pelo
// módulo social, casando com o 402 do backend.
useModuleGate(['social_studio', 'marketing']);

const { t } = useI18n();

const loading = ref(true);
const saving = ref(false);
const status = ref({ configured: false, connected: false, ready: false });
const form = reactive({
  customer_id: '',
  login_customer_id: '',
  conversion_action_id: '',
  developer_token: '',
  enabled: false,
});

const tk = key => t(`INTEGRATION_SETTINGS.GOOGLE_ADS.${key}`);

// Descoberta automática (contas + ações de conversão via API).
const accounts = ref([]);
const conversionActions = ref([]);
const loadingAccounts = ref(false);
const loadingActions = ref(false);
const creatingAction = ref(false);

// Desempenho das campanhas (puxado do Google Ads).
const campaigns = ref([]);
const loadingCampaigns = ref(false);
const perfDays = ref(30);

const brl = value =>
  (value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
const num = value => (value || 0).toLocaleString('pt-BR');
const costPerConv = c => (c.conversions > 0 ? c.cost / c.conversions : null);

const loadCampaigns = async () => {
  if (!form.customer_id) {
    campaigns.value = [];
    return;
  }
  loadingCampaigns.value = true;
  try {
    const { data } = await googleAdsAPI.getCampaigns(
      form.customer_id,
      perfDays.value
    );
    campaigns.value = data.campaigns || [];
  } catch {
    campaigns.value = [];
  } finally {
    loadingCampaigns.value = false;
  }
};

// Conversões e clientes pela ótica do NOSSO CRM (quem virou cliente de verdade).
const crm = ref(null);
const loadingCrm = ref(false);
const loadCrm = async () => {
  loadingCrm.value = true;
  try {
    const { data } = await googleAdsAPI.getCrmConversions(perfDays.value);
    crm.value = data;
  } catch {
    crm.value = null;
  } finally {
    loadingCrm.value = false;
  }
};

// Gasto e conversões do Google (somados das campanhas do período).
const totalCost = computed(() =>
  campaigns.value.reduce((s, c) => s + (c.cost || 0), 0)
);
const totalGoogleConv = computed(() =>
  campaigns.value.reduce((s, c) => s + (c.conversions || 0), 0)
);
const coveragePct = computed(() => {
  const cov = crm.value?.gclid_coverage;
  if (!cov || !cov.total_leads) return null;
  return Math.round((cov.leads_with_gclid / cov.total_leads) * 100);
});
// Alarme: gastou, o Google não marcou conversão e quase nenhum lead tem gclid.
const showAttributionAlert = computed(
  () =>
    totalCost.value > 0 &&
    totalGoogleConv.value === 0 &&
    coveragePct.value === 0
);
const fmtDate = d =>
  d
    ? new Date(d).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
      })
    : '—';

const setPeriod = days => {
  perfDays.value = days;
  loadCampaigns();
  loadCrm();
};

const togglingId = ref(null);
const toggleCampaign = async c => {
  const newStatus = c.status === 'ENABLED' ? 'PAUSED' : 'ENABLED';
  togglingId.value = c.id;
  try {
    await googleAdsAPI.updateCampaign(form.customer_id, c.id, newStatus);
    c.status = newStatus;
    useAlert(
      tk(newStatus === 'ENABLED' ? 'CAMPAIGN_RESUMED' : 'CAMPAIGN_PAUSED')
    );
  } catch (error) {
    useAlert(error?.response?.data?.error || tk('CAMPAIGN_TOGGLE_ERROR'));
  } finally {
    togglingId.value = null;
  }
};

const loadConversionActions = async () => {
  if (!form.customer_id) {
    conversionActions.value = [];
    return;
  }
  loadingActions.value = true;
  try {
    const { data } = await googleAdsAPI.getConversionActions(form.customer_id);
    conversionActions.value = data.conversion_actions || [];
    // Uma ação de importação só → auto-seleciona (caso comum).
    if (conversionActions.value.length === 1 && !form.conversion_action_id) {
      form.conversion_action_id = conversionActions.value[0].id;
    }
  } catch {
    conversionActions.value = [];
  } finally {
    loadingActions.value = false;
  }
};

const loadAccounts = async () => {
  loadingAccounts.value = true;
  try {
    const { data } = await googleAdsAPI.getAccounts();
    // Só contas de anúncios — gerenciadores (MCC) não recebem conversão.
    accounts.value = (data.accounts || []).filter(a => !a.manager);
    // Uma conta só → auto-seleciona. Várias → o cliente escolhe no dropdown.
    if (accounts.value.length === 1 && !form.customer_id) {
      form.customer_id = accounts.value[0].id;
    }
    if (form.customer_id) loadConversionActions();
  } catch {
    accounts.value = [];
  } finally {
    loadingAccounts.value = false;
  }
};

const onSelectAccount = () => {
  form.conversion_action_id = '';
  conversionActions.value = [];
  loadConversionActions();
};

// Cria a ação de conversão pelo nosso sistema (sem abrir o Google Ads).
const createConversionAction = async () => {
  if (!form.customer_id) return;
  creatingAction.value = true;
  try {
    const { data } = await googleAdsAPI.createConversionAction(
      form.customer_id
    );
    useAlert(tk('CREATE_ACTION_SUCCESS'));
    await loadConversionActions();
    if (data.conversion_action?.id) {
      form.conversion_action_id = data.conversion_action.id;
    }
  } catch (error) {
    useAlert(error?.response?.data?.error || tk('CREATE_ACTION_ERROR'));
  } finally {
    creatingAction.value = false;
  }
};

const fetchConfig = async () => {
  loading.value = true;
  try {
    const { data } = await googleAdsAPI.getConfig();
    status.value = data;
    form.customer_id = data.customer_id || '';
    form.login_customer_id = data.login_customer_id || '';
    form.conversion_action_id = data.conversion_action_id || '';
    form.enabled = data.enabled || false;
    if (data.connected) loadAccounts();
    if (data.connected && data.customer_id) {
      loadCampaigns();
      loadCrm();
    }
  } catch {
    status.value = { configured: false, connected: false, ready: false };
  } finally {
    loading.value = false;
  }
};

const onOauthMessage = event => {
  if (event.data?.type !== 'google-ads-oauth') return;
  if (event.data.connected) {
    useAlert(tk('CONNECTED'));
    fetchConfig();
  } else {
    useAlert(tk('CONNECT_ERROR'));
  }
};

const connect = async () => {
  try {
    const { data } = await googleAdsAPI.authorizeUrl();
    window.open(
      data.authorization_url,
      'google-ads-oauth',
      'width=600,height=720'
    );
  } catch {
    useAlert(tk('CONNECT_ERROR'));
  }
};

const save = async () => {
  saving.value = true;
  try {
    const { data } = await googleAdsAPI.saveConfig({ ...form });
    status.value = data;
    useAlert(tk('SAVED'));
  } catch {
    useAlert(tk('SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const disconnect = async () => {
  try {
    await googleAdsAPI.removeConfig();
    useAlert(tk('DISCONNECTED'));
    fetchConfig();
  } catch {
    useAlert(tk('SAVE_ERROR'));
  }
};

const statusLabel = computed(() => {
  if (status.value.ready) return tk('STATUS_READY');
  if (status.value.connected) return tk('STATUS_CONNECTED_INCOMPLETE');
  return tk('STATUS_DISCONNECTED');
});

// Palavras-chave por campanha (modal dedicado, aberto pela tabela de desempenho).
const kwModalOpen = ref(false);
const kwModalCampaign = ref(null);
const keywordsByCampaign = ref({});
const kwLoading = ref(false);
const kwForm = ref({ text: '', matchType: 'BROAD' });
const kwSubmitting = ref(false);
const kwTogglingId = ref(null);

const kwCurrentList = computed(
  () => keywordsByCampaign.value[kwModalCampaign.value?.id] || []
);

async function fetchKeywords(campaignId) {
  kwLoading.value = true;
  try {
    const { data } = await googleAdsAPI.getKeywords(
      form.customer_id,
      campaignId
    );
    keywordsByCampaign.value = {
      ...keywordsByCampaign.value,
      [campaignId]: data.keywords || [],
    };
  } catch {
    keywordsByCampaign.value = {
      ...keywordsByCampaign.value,
      [campaignId]: [],
    };
  } finally {
    kwLoading.value = false;
  }
}

function openKeywords(campaign) {
  kwModalCampaign.value = campaign;
  kwForm.value = { text: '', matchType: 'BROAD' };
  kwModalOpen.value = true;
  fetchKeywords(campaign.id);
}

function closeKeywords() {
  kwModalOpen.value = false;
  kwModalCampaign.value = null;
}

async function submitKeywords(campaignId) {
  const lines = kwForm.value.text
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);
  if (!lines.length) return;
  kwSubmitting.value = true;
  try {
    const { data } = await googleAdsAPI.addKeywords(
      form.customer_id,
      campaignId,
      lines,
      kwForm.value.matchType
    );
    const added = data.added ?? 0;
    const skipped = data.skipped ?? 0;
    useAlert(
      skipped > 0
        ? t('INTEGRATION_SETTINGS.GOOGLE_ADS.KEYWORDS.ADDED_RESULT', {
            added,
            skipped,
          })
        : t('INTEGRATION_SETTINGS.GOOGLE_ADS.KEYWORDS.ADDED_ONLY', { added })
    );
    kwForm.value = { text: '', matchType: 'BROAD' };
    await fetchKeywords(campaignId);
  } catch (error) {
    useAlert(error?.response?.data?.error || tk('CAMPAIGN_TOGGLE_ERROR'));
  } finally {
    kwSubmitting.value = false;
  }
}

async function toggleKeyword(campaignId, kw) {
  const newStatus = kw.status === 'ENABLED' ? 'PAUSED' : 'ENABLED';
  kwTogglingId.value = kw.criterion_id;
  try {
    await googleAdsAPI.updateKeyword(
      form.customer_id,
      kw.ad_group_id,
      kw.criterion_id,
      newStatus
    );
    await fetchKeywords(campaignId);
  } catch (error) {
    useAlert(error?.response?.data?.error || tk('CAMPAIGN_TOGGLE_ERROR'));
  } finally {
    kwTogglingId.value = null;
  }
}

onMounted(() => {
  window.addEventListener('message', onOauthMessage);
  fetchConfig();
});
onUnmounted(() => window.removeEventListener('message', onOauthMessage));
</script>

<template>
  <div class="w-full p-6">
    <div class="mb-5">
      <h2 class="text-lg font-semibold text-n-slate-12">{{ tk('TITLE') }}</h2>
      <p class="text-sm text-n-slate-11">{{ tk('DESCRIPTION') }}</p>
    </div>

    <div v-if="loading" class="text-sm text-n-slate-10">
      {{ tk('CONNECTING') }}
    </div>

    <div v-else class="flex flex-col gap-5">
      <!-- Conexão -->
      <div
        class="flex items-center justify-between rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div class="flex items-center gap-2.5">
          <span
            class="h-2.5 w-2.5 rounded-full"
            :class="
              status.ready
                ? 'bg-n-teal-9'
                : status.connected
                  ? 'bg-n-amber-9'
                  : 'bg-n-slate-8'
            "
          />
          <span class="text-sm font-medium text-n-slate-12">{{
            statusLabel
          }}</span>
        </div>
        <div class="flex gap-2">
          <button
            type="button"
            class="rounded-lg bg-n-brand px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            @click="connect"
          >
            {{ status.connected ? tk('RECONNECT') : tk('CONNECT') }}
          </button>
          <button
            v-if="status.connected"
            type="button"
            class="rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            @click="disconnect"
          >
            {{ tk('DISCONNECT') }}
          </button>
        </div>
      </div>

      <!-- Config -->
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-11">{{
            tk('CUSTOMER_ID')
          }}</span>
          <span v-if="loadingAccounts" class="text-xs text-n-slate-10">{{
            tk('LOADING_ACCOUNTS')
          }}</span>
          <div v-else-if="accounts.length" class="relative">
            <select
              v-model="form.customer_id"
              class="w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 bg-none px-3 py-2 pr-9 text-sm text-n-slate-12"
              @change="onSelectAccount"
            >
              <option value="">{{ tk('SELECT_ACCOUNT') }}</option>
              <option v-for="a in accounts" :key="a.id" :value="a.id">
                {{ a.name }} — {{ a.id }}
              </option>
            </select>
            <span
              class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
            />
          </div>
          <input
            v-else
            v-model="form.customer_id"
            type="text"
            :placeholder="tk('CUSTOMER_ID_PH')"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-11">{{
            tk('LOGIN_CUSTOMER_ID')
          }}</span>
          <input
            v-model="form.login_customer_id"
            type="text"
            :placeholder="tk('LOGIN_CUSTOMER_ID_PH')"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-11">{{
            tk('CONVERSION_ACTION_ID')
          }}</span>
          <span v-if="loadingActions" class="text-xs text-n-slate-10">{{
            tk('LOADING_ACTIONS')
          }}</span>
          <div v-else-if="conversionActions.length" class="relative">
            <select
              v-model="form.conversion_action_id"
              class="w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 bg-none px-3 py-2 pr-9 text-sm text-n-slate-12"
            >
              <option value="">{{ tk('SELECT_ACTION') }}</option>
              <option v-for="c in conversionActions" :key="c.id" :value="c.id">
                {{ c.name }} — {{ c.id }}
              </option>
            </select>
            <span
              class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
            />
          </div>
          <input
            v-else
            v-model="form.conversion_action_id"
            type="text"
            :placeholder="tk('CONVERSION_ACTION_ID_PH')"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          />
          <span
            v-if="
              !loadingActions && form.customer_id && !conversionActions.length
            "
            class="text-xs text-n-amber-11"
          >
            {{ tk('NO_ACTIONS_HINT') }}
          </span>
          <button
            v-if="form.customer_id && !loadingActions"
            type="button"
            class="mt-1 self-start rounded-lg border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
            :disabled="creatingAction"
            @click="createConversionAction"
          >
            {{ creatingAction ? tk('CREATING_ACTION') : tk('CREATE_ACTION') }}
          </button>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-11">{{
            tk('DEVELOPER_TOKEN')
          }}</span>
          <input
            v-model="form.developer_token"
            type="text"
            :placeholder="tk('DEVELOPER_TOKEN_PH')"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          />
        </label>
        <label class="flex cursor-pointer items-center gap-2 md:col-span-2">
          <input
            v-model="form.enabled"
            type="checkbox"
            class="h-4 w-4 accent-n-brand"
          />
          <span class="text-sm text-n-slate-12">{{ tk('ENABLE') }}</span>
        </label>
      </div>

      <div>
        <button
          type="button"
          class="rounded-lg bg-n-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? tk('SAVING') : tk('SAVE') }}
        </button>
      </div>

      <!-- Desempenho das campanhas -->
      <div v-if="form.customer_id" class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ tk('RESULTS_TITLE') }}
          </h3>
          <div class="flex gap-1 rounded-lg border border-n-weak p-0.5">
            <button
              v-for="d in [7, 30]"
              :key="d"
              type="button"
              class="rounded-md px-2.5 py-1 text-xs font-medium"
              :class="
                perfDays === d
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="setPeriod(d)"
            >
              {{ d }}{{ tk('PERIOD_DAYS') }}
            </button>
          </div>
        </div>

        <!-- Conversões e clientes pela ótica do NOSSO CRM -->
        <p class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
          {{ tk('CRM.TITLE') }}
        </p>
        <div
          v-if="showAttributionAlert"
          class="flex items-start gap-2 rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-3 text-sm text-n-ruby-11"
        >
          <span class="i-lucide-triangle-alert mt-0.5 shrink-0" />
          <span>{{ tk('CRM.ALERT') }}</span>
        </div>

        <div v-if="crm" class="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div class="rounded-lg border border-n-weak p-3">
            <p class="text-xs text-n-slate-10">{{ tk('CRM.SPEND') }}</p>
            <p class="mt-1 text-lg font-semibold tabular-nums text-n-slate-12">
              {{ brl(totalCost) }}
            </p>
          </div>
          <div class="rounded-lg border border-n-weak p-3">
            <p class="text-xs text-n-slate-10">{{ tk('CRM.WON') }}</p>
            <p class="mt-1 text-lg font-semibold tabular-nums text-n-teal-11">
              {{ crm.won.count }}
              <span class="text-sm font-normal text-n-slate-11">
                · {{ brl(crm.won.value_brl) }}
              </span>
            </p>
          </div>
          <div class="rounded-lg border border-n-weak p-3">
            <p class="text-xs text-n-slate-10">{{ tk('CRM.ATTRIBUTABLE') }}</p>
            <p class="mt-1 text-lg font-semibold tabular-nums text-n-slate-12">
              {{ crm.attributable.count }}
              <span class="text-sm font-normal text-n-slate-11">
                · {{ brl(crm.attributable.value_brl) }}
              </span>
            </p>
          </div>
          <div class="rounded-lg border border-n-weak p-3">
            <p class="text-xs text-n-slate-10">{{ tk('CRM.COVERAGE') }}</p>
            <p class="mt-1 text-lg font-semibold tabular-nums text-n-slate-12">
              {{ coveragePct === null ? '—' : `${coveragePct}%` }}
              <span class="text-sm font-normal text-n-slate-11">
                {{ crm.gclid_coverage.leads_with_gclid }}/{{
                  crm.gclid_coverage.total_leads
                }}
              </span>
            </p>
          </div>
        </div>

        <div v-if="crm && crm.customers.length" class="flex flex-col gap-1.5">
          <p class="text-xs font-medium text-n-slate-11">
            {{ tk('CRM.CUSTOMERS_TITLE') }}
          </p>
          <div
            class="max-h-[420px] overflow-auto rounded-lg border border-n-weak"
          >
            <table class="w-full text-sm">
              <thead class="sticky top-0 bg-n-solid-2">
                <tr
                  class="border-b border-n-weak text-left text-xs text-n-slate-10"
                >
                  <th class="px-3 py-2 font-medium">
                    {{ tk('CRM.COL_CUSTOMER') }}
                  </th>
                  <th class="px-3 py-2 text-right font-medium">
                    {{ tk('CRM.COL_VALUE') }}
                  </th>
                  <th class="px-3 py-2 text-right font-medium">
                    {{ tk('CRM.COL_DATE') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(cust, i) in crm.customers"
                  :key="i"
                  class="border-b border-n-weak last:border-0"
                >
                  <td class="px-3 py-2 text-n-slate-12">{{ cust.name }}</td>
                  <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                    {{ brl(cust.value_brl) }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                    {{ fmtDate(cust.closed_at) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          v-else-if="crm && !loadingCrm"
          class="rounded-lg border border-n-weak bg-n-solid-1 p-4 text-sm text-n-slate-10"
        >
          {{ tk('CRM.EMPTY') }}
        </div>

        <h3 class="mt-2 text-sm font-semibold text-n-slate-12">
          {{ tk('PERFORMANCE_TITLE') }}
        </h3>

        <div v-if="loadingCampaigns" class="text-sm text-n-slate-10">
          {{ tk('LOADING_CAMPAIGNS') }}
        </div>
        <div
          v-else-if="!campaigns.length"
          class="rounded-lg border border-n-weak bg-n-solid-1 p-4 text-sm text-n-slate-10"
        >
          {{ tk('NO_CAMPAIGNS') }}
        </div>
        <div v-else class="overflow-x-auto rounded-lg border border-n-weak">
          <table class="w-full text-sm">
            <thead>
              <tr
                class="border-b border-n-weak text-left text-xs text-n-slate-10"
              >
                <th class="px-3 py-2 font-medium">{{ tk('COL_CAMPAIGN') }}</th>
                <th class="px-3 py-2 text-right font-medium">
                  {{ tk('COL_IMPRESSIONS') }}
                </th>
                <th class="px-3 py-2 text-right font-medium">
                  {{ tk('COL_CLICKS') }}
                </th>
                <th class="px-3 py-2 text-right font-medium">
                  {{ tk('COL_CTR') }}
                </th>
                <th class="px-3 py-2 text-right font-medium">
                  {{ tk('COL_COST') }}
                </th>
                <th class="px-3 py-2 text-right font-medium">
                  {{ tk('COL_CONVERSIONS') }}
                </th>
                <th class="px-3 py-2 text-right font-medium">
                  {{ tk('COL_COST_CONV') }}
                </th>
                <th class="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              <template v-for="c in campaigns" :key="c.id">
                <tr class="border-b border-n-weak last:border-0">
                  <td class="px-3 py-2">
                    <div class="flex items-center gap-2">
                      <span class="text-n-slate-12">{{ c.name }}</span>
                      <span
                        class="rounded px-1.5 py-0.5 text-[10px] font-medium"
                        :class="
                          c.status === 'ENABLED'
                            ? 'bg-n-teal-3 text-n-teal-11'
                            : 'bg-n-slate-3 text-n-slate-11'
                        "
                      >
                        {{
                          c.status === 'ENABLED'
                            ? tk('CAMPAIGN_ON')
                            : tk('CAMPAIGN_OFF')
                        }}
                      </span>
                    </div>
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                    {{ num(c.impressions) }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                    {{ num(c.clicks) }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                    {{ c.ctr }}%
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                    {{ brl(c.cost) }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                    {{ c.conversions }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums text-n-slate-11">
                    {{ costPerConv(c) === null ? '—' : brl(costPerConv(c)) }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    <button
                      type="button"
                      class="rounded-md border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 ltr:mr-1 rtl:ml-1"
                      @click="openKeywords(c)"
                    >
                      {{ tk('KEYWORDS.SHOW') }}
                    </button>
                    <button
                      type="button"
                      class="rounded-md border border-n-weak p-1.5 hover:bg-n-alpha-2 disabled:opacity-40"
                      :disabled="togglingId === c.id"
                      :title="
                        c.status === 'ENABLED'
                          ? tk('PAUSE_CTA')
                          : tk('PLAY_CTA')
                      "
                      @click="toggleCampaign(c)"
                    >
                      <span
                        class="block size-4 text-n-slate-11"
                        :class="
                          c.status === 'ENABLED'
                            ? 'i-lucide-pause'
                            : 'i-lucide-play'
                        "
                      />
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <woot-modal v-model:show="kwModalOpen" :on-close="closeKeywords">
    <woot-modal-header
      :header-title="`${tk('KEYWORDS.TITLE')} — ${kwModalCampaign?.name || ''}`"
    />
    <div class="p-8 pt-4">
      <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4 mb-5">
        <div class="text-sm font-medium text-n-slate-12 mb-2">
          {{ tk('KEYWORDS.ADD_TITLE') }}
        </div>
        <textarea
          v-model="kwForm.text"
          rows="4"
          :placeholder="tk('KEYWORDS.ADD_PLACEHOLDER')"
          class="w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30"
        />
        <div class="flex items-center gap-2 mt-3">
          <select
            v-model="kwForm.matchType"
            class="rounded-md border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12"
          >
            <option value="BROAD">{{ tk('KEYWORDS.MATCH_BROAD') }}</option>
            <option value="PHRASE">{{ tk('KEYWORDS.MATCH_PHRASE') }}</option>
            <option value="EXACT">{{ tk('KEYWORDS.MATCH_EXACT') }}</option>
          </select>
          <button
            type="button"
            class="rounded-md bg-n-brand px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            :disabled="kwSubmitting || !kwForm.text.trim()"
            @click="submitKeywords(kwModalCampaign.id)"
          >
            {{ kwSubmitting ? tk('KEYWORDS.ADDING') : tk('KEYWORDS.ADD_CTA') }}
          </button>
        </div>
      </div>

      <div v-if="kwLoading" class="text-sm text-n-slate-10 py-2">
        {{ tk('KEYWORDS.LOADING') }}
      </div>
      <div
        v-else-if="!kwCurrentList.length"
        class="text-sm text-n-slate-10 py-2"
      >
        {{ tk('KEYWORDS.EMPTY') }}
      </div>
      <div v-else class="flex flex-col gap-1 max-h-[45vh] overflow-y-auto">
        <div
          v-for="kw in kwCurrentList"
          :key="kw.criterion_id"
          class="flex items-center gap-2 rounded-md bg-n-solid-1 px-3 py-1.5"
        >
          <span class="flex-1 text-n-slate-12">{{ kw.text }}</span>
          <span
            class="rounded bg-n-slate-3 px-1.5 py-0.5 text-[10px] text-n-slate-11"
          >
            {{ tk(`KEYWORDS.MATCH_${kw.match_type}`) }}
          </span>
          <span class="text-[11px] text-n-slate-10 tabular-nums">
            {{ num(kw.impressions) }} {{ tk('KEYWORDS.IMPRESSIONS') }}
          </span>
          <span
            class="rounded px-1.5 py-0.5 text-[10px] font-medium"
            :class="
              kw.status === 'ENABLED'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-slate-3 text-n-slate-11'
            "
          >
            {{
              kw.status === 'ENABLED' ? tk('KEYWORDS.ON') : tk('KEYWORDS.OFF')
            }}
          </span>
          <button
            type="button"
            class="rounded-md border border-n-weak p-1 hover:bg-n-alpha-2 disabled:opacity-40"
            :disabled="kwTogglingId === kw.criterion_id"
            :title="
              kw.status === 'ENABLED'
                ? tk('KEYWORDS.PAUSE')
                : tk('KEYWORDS.ENABLE')
            "
            @click="toggleKeyword(kwModalCampaign.id, kw)"
          >
            <span
              class="block size-3.5 text-n-slate-11"
              :class="
                kw.status === 'ENABLED' ? 'i-lucide-pause' : 'i-lucide-play'
              "
            />
          </button>
        </div>
      </div>
    </div>
  </woot-modal>
</template>
